import axios from 'axios'
import jwtDefaultConfig from './jwtDefaultConfig'
import { AuthControllerApi, settings } from '@api'
import { clearClientSession, getStoredAccessToken } from '@src/auth/session'

const api = new AuthControllerApi(settings)

export default class JwtService {
  // ** jwtConfig <= Will be used by this service
  jwtConfig = { ...jwtDefaultConfig }

  // ** For Refreshing Token
  isAlreadyFetchingAccessToken = false

  // ** For Refreshing Token
  subscribers = []

  constructor(jwtOverrideConfig) {
    this.jwtConfig = { ...this.jwtConfig, ...jwtOverrideConfig }

    // ** Request Interceptor
    axios.interceptors.request.use(
      config => {
        // ** Get token from localStorage
        const accessToken = this.getToken()

        // ** If token is present add it to request's Authorization Header
        if (accessToken) {
          config.headers = config.headers || {}
          // ** eslint-disable-next-line no-param-reassign
          config.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
        }
        return config
      },
      error => Promise.reject(error)
    )

    // ** Add request/response interceptor
    axios.interceptors.response.use(
      response => response,
      error => {
        // ** const { config, response: { status } } = error
        const { config, response } = error
        const originalRequest = config

        // ** if (status === 401) {
        if (response && response.status === 401) {
          if (originalRequest?._retry) {
            this.clearSessionAndRedirect(error)
            return Promise.reject(error)
          }

          // Si el propio endpoint de refresh falla con 401, hay que salir inmediatamente
          // para evitar un deadlock donde el interceptor se queda colgado indefinidamente.
          if (originalRequest?.url?.includes(this.jwtConfig.refreshEndpoint)) {
            this.clearSessionAndRedirect(error)
            return Promise.reject(error)
          }

          const refreshToken = this.getRefreshToken()
          if (!refreshToken) {
            this.clearSessionAndRedirect(error)
            return Promise.reject(error)
          }

          if (!this.isAlreadyFetchingAccessToken) {
            this.isAlreadyFetchingAccessToken = true
            this.refreshToken().then(r => {
              this.isAlreadyFetchingAccessToken = false

              // ** Update accessToken in localStorage
              this.setToken(r.data.accessToken)
              // Solo actualizar el refreshToken si el endpoint devuelve uno nuevo
              // (el endpoint /auth/refresh-token solo devuelve accessToken)
              if (r.data.refreshToken) {
                this.setRefreshToken(r.data.refreshToken)
              }

              // ** Also update userData.accessToken so getAccessToken() returns the new token
              try {
                const userData = JSON.parse(localStorage.getItem('userData'))
                if (userData) {
                  userData.accessToken = r.data.accessToken
                  localStorage.setItem('userData', JSON.stringify(userData))
                }
              } catch (e) {
                console.error('Error actualizando userData tras refresh de token', e)
              }

              this.onAccessTokenFetched(r.data.accessToken)
            }).catch(refreshError => {
              this.clearSessionAndRedirect(refreshError)
            })
          }
          const retryOriginalRequest = new Promise((resolve, reject) => {
            this.addSubscriber(accessToken => {
              // ** Make sure to assign accessToken according to your response.
              // ** Check: https://pixinvent.ticksy.com/ticket/2413870
              // ** Change Authorization header
              originalRequest._retry = true
              originalRequest.headers = originalRequest.headers || {}
              originalRequest.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
              resolve(axios(originalRequest))
            }, reject)
          })
          return retryOriginalRequest
        }
        return Promise.reject(error)
      }
    )
  }

  clearSessionAndRedirect(error) {
    this.isAlreadyFetchingAccessToken = false
    this.onAccessTokenFetchError(error)
    clearClientSession()

    if (window.location.pathname !== '/login') {
      window.location.replace('/login')
    }
  }

  onAccessTokenFetched(accessToken) {
    this.subscribers.forEach(subscriber => subscriber.resolve(accessToken))
    this.subscribers = []
  }

  onAccessTokenFetchError(error) {
    this.subscribers.forEach(subscriber => subscriber.reject(error))
    this.subscribers = []
  }

  addSubscriber(resolve, reject) {
    this.subscribers.push({ resolve, reject })
  }

  getToken() {
    return getStoredAccessToken()
  }

  getRefreshToken() {
    return localStorage.getItem(this.jwtConfig.storageRefreshTokenKeyName)
  }

  setToken(value) {
    localStorage.setItem(this.jwtConfig.storageTokenKeyName, value)
  }

  setRefreshToken(value) {
    localStorage.setItem(this.jwtConfig.storageRefreshTokenKeyName, value)
  }

  login(...args) {
    // return axios.post(this.jwtConfig.loginEndpoint, ...args)
    return api.authControllerLogin(...args)
  }

  loginRefresh(...args) {
    return api.authControllerRefreshLogin(...args)
  }

  register(...args) {
    return axios.post(this.jwtConfig.registerEndpoint, ...args)
  }

  refreshToken() {
    return axios.post(this.jwtConfig.refreshEndpoint, {
      refreshToken: this.getRefreshToken()
    })
  }
}